import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import i18n from "i18next";
import type { AppDispatch, AppThunk } from "../../../app/store/store";

export interface ITokenPayload {
  authorities: [];
  sub: string | undefined;
  iat: number;
  exp: number;
  id: string | undefined;
  name: string | undefined;
  email: string | undefined;
  clientId: number;
  device: string | undefined;
  application: string | undefined;
}

export interface IUserPayload {
  userId: string;
  fullName: string;
  email: string;
  clientDb?: {
    id: string;
    name: string;
  };
}

export interface IUserState {
  error: string | undefined;
  uiLanguage: string;
  scanMode: "default" | "autoMode" | "semiAutoMode";
  useLocation: boolean,
  useObject: boolean;
  useLot: boolean;
  user: IUserPayload | null;
  showOnlyNotCollected: boolean;
  expandRows: boolean;
  refreshData: boolean;
  useExtraFields: boolean;
  extraFieldsArray: [];
  useCamera: boolean;
  cameraId: string;
  departmentId: string;
  barcodeSeparator: string;
  useSearch: boolean;
  deleteRow: boolean;
  changeVersion?: boolean;
  fieldsOrder: string[];
  barcodeSearch: "N" | "S" | "P";
  objectSearch: "N" | "S" | "P" ;
  locationSearch: "N" | "S" | "P";
  lotSearch: "N" | "S" | "P";
  object2Search: "N" | "S" | "P";
  lot2Search: "N" | "S" | "P";
  useObject2: boolean;
  useLot2: boolean;
  useExpDate: boolean;
  useExpDate2: boolean;
  userName: string;
}

const initialUserLocalItem = localStorage.getItem("lastUser");
let initialUser = null;
try {
  if (initialUserLocalItem) {
    initialUser = JSON.parse(initialUserLocalItem);
  }
  // eslint-disable-next-line no-empty
} catch (err) {}

const initialState: IUserState = {
  error: undefined,
  uiLanguage: "lt",
  scanMode: "default",
  useLocation: false,
  useObject: false,
  useLot: false,
  showOnlyNotCollected: false,
  expandRows: false,
  refreshData: false,
  useExtraFields: false,
  extraFieldsArray: [],
  useCamera: false,
  cameraId: "",
  departmentId: "",
  barcodeSeparator: ";",
  useSearch: false,
  user: initialUser,
  deleteRow: false,
  changeVersion: false,
  fieldsOrder: [
  "lokacija",
  "barkodas",
  "kiekis",
  "objektas",
  "objektas2",
  "serija",
  "serija2",
  "galiojimodata",
  "galiojimodata2",
  "papildomi",
],
  barcodeSearch: "N",
  objectSearch: "N",
  lotSearch: "N",
  locationSearch: "N",
  object2Search: "N",
  lot2Search: "N",
  useObject2: false,
  useLot2: false,
  useExpDate: false,
  useExpDate2: false,
  userName: "",
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<IUserPayload>) {
      state.user = action.payload;
    },
    clearUser(state) {
      state.user = null;
    },
    setUiLanguage(state, action: PayloadAction<string>) {
      state.uiLanguage = action.payload;
    },
    setScanMode(
      state,
      action: PayloadAction<"default" | "autoMode" | "semiAutoMode">
    ) {
      state.scanMode = action.payload;
    },
    setLocation(state, action: PayloadAction<boolean>) {
      state.useLocation = action.payload;
    },
    setObject(state, action: PayloadAction<boolean>) {
      state.useObject = action.payload;
    },
    setLot(state, action: PayloadAction<boolean>) {
      state.useLot = action.payload;
    },
    setShowOnlyNotCollected(state, action: PayloadAction<boolean>) {
      state.showOnlyNotCollected = action.payload;
    },
    setExpandRows(state, action: PayloadAction<boolean>) {
      state.expandRows = action.payload;
    },
    setRefreshData(state, action: PayloadAction<boolean>) {
      state.refreshData = action.payload;
    },
    setDbId(
      state,
      action: PayloadAction<{ id: number; name: string; userId: string }>
    ) {
      if (state.user) {
        state.user.clientDb = {
          id: action.payload.id.toString(),
          name: action.payload.name,
        };
      }
    },
    setExtraFields(state: any, action: PayloadAction<boolean>) {
      state.useExtraFields = action.payload;
    },
    setExtraFieldsArray(state: any, action: PayloadAction<Array<any>>) {
      state.extraFieldsArray = action.payload;
    },
    setCamera(state, action: PayloadAction<boolean>) {
      state.useCamera = action.payload;
    },
    setCameraId(state, action: PayloadAction<string>) {
      state.cameraId = action.payload;
    },
    setDepartmentId(state, action: PayloadAction<string>) {
      state.departmentId = action.payload;
    },
    setBarcodeSeparator(state, action: PayloadAction<string>) {
      state.barcodeSeparator = action.payload;
    },
    setSearch(state: any, action: PayloadAction<boolean>) {
      state.useSearch = action.payload;
    },
    setDeleteRow(state: any, action: PayloadAction<boolean>) {
      state.deleteRow = action.payload;
    },
    setChangeVersion(state: any, action: PayloadAction<boolean>) {
      state.changeVersion = action.payload;
    },
    setFieldsOrder(state: any, action: PayloadAction<string[]>) {
      state.fieldsOrder = action.payload;
    },
    setBarcodeSearch(state: any, action: PayloadAction<"N" | "S" | "P">) {
      state.barcodeSearch = action.payload;
    },
    setObjectSearch(state: any, action: PayloadAction<"N" | "S" | "P">) {
      state.objectSearch = action.payload;
    },
    setLocationSearch(state: any, action: PayloadAction<"N" | "S" | "P">) {
      state.objectSearch = action.payload;
    },
    setLotSearch(state: any, action: PayloadAction<"N" | "S" | "P">) {
      state.lotSearch = action.payload;
    },
    setObject2Search(state: any, action: PayloadAction<"N" | "S" | "P">) {
      state.object2Search = action.payload;
    },
    setLot2Search(state: any, action: PayloadAction<"N" | "S" | "P">) {
      state.lot2Search = action.payload;
    },
    setObject2(state, action: PayloadAction<boolean>) {
      state.useObject2 = action.payload;
    },
    setLot2(state, action: PayloadAction<boolean>) {
      state.useLot2 = action.payload;
    },
    setExpDate(state, action: PayloadAction<boolean>) {
      state.useExpDate = action.payload;
    },
    setExpDate2(state, action: PayloadAction<boolean>) {
      state.useExpDate2 = action.payload;
    },
    setUserName(state, action: PayloadAction<string>) { 
      state.userName = action.payload; 
    }
  },
});

export const setUserDetails =
  (user: IUserPayload): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setUser(user));
    localStorage.setItem("lastUser", JSON.stringify(user));
  };

export const clearUserDetails = () => async (dispatch: AppDispatch) =>
  dispatch(userSlice.actions.clearUser());

export const setUiLanguage =
  (language: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    i18n
      .changeLanguage(language)
      .then(() => {
        dispatch(userSlice.actions.setUiLanguage(language));
        localStorage.setItem("lastLanguage", language);
      })
      .catch((error) => {
        console.error(
          `failed to switch UI language with error: ${error.message}`
        );
      });
  };

export const setUserDbId =
  (userDb: { id: number; name: string; userId: string }): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setDbId(userDb));
  };

export const setUserScanMode =
  (mode: "default" | "autoMode" | "semiAutoMode"): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setScanMode(mode));
  };

export const setUserLocationUsage =
  (useLocation: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setLocation(useLocation));
  };

export const setUserObjectUsage =
  (useObect: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setObject(useObect));
  };

export const setUserLotUsage =
  (useLOt: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setLot(useLOt));
  };

export const setUserShowOnlyNotCollected =
  (show: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setShowOnlyNotCollected(show));
  };

export const setUserExpandRows =
  (allow: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setExpandRows(allow));
  };

export const setUserRefreshData =
  (refresh: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setRefreshData(refresh));
  };

export const setUserExtraFieldsUsage =
  (useExtra: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setExtraFields(useExtra));
  };

export const setUserExtraFieldsArray =
  (useExtra: []): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setExtraFieldsArray(useExtra));
  };

export const setUserCameraUsage =
  (useCamera: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setCamera(useCamera));
  };

export const setUserCameraId =
  (cameraId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setCameraId(cameraId));
  };

export const setUserDepartmentId =
  (departmentId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setDepartmentId(departmentId));
  };

export const setUserBarcodeSeparator =
  (separator: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setBarcodeSeparator(separator));
  };

export const setUserSearchUsage =
  (useSearch: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setSearch(useSearch));
  };

export const setUserRowDeletion =
  (deleteRow: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setDeleteRow(deleteRow));
  };

export const setUserChangeVersion =
  (change: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setChangeVersion(change));
  };

export const setUserFieldsOrder =
  (fieldsOrder: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    const parsed = fieldsOrder.split(',').map(f => f.trim());
    dispatch(userSlice.actions.setFieldsOrder(parsed));
  };

export const setUserBarcodeSearch =
  (search: "N" | "S" | "P"): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setBarcodeSearch(search));
  };

export const setUserObjectSearch =
  (search: "N" | "S" | "P"): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setObjectSearch(search));
  };

export const setUserLocationSearch =
  (search: "N" | "S" | "P"): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setLocationSearch(search));
  };

export const setUserLotSearch =
  (search: "N" | "S" | "P"): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setLotSearch(search));
  };

export const setUserObject2Search =
  (search: "N" | "S" | "P"): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setObject2Search(search));
  };

export const setUserLot2Search =
  (search: "N" | "S" | "P"): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setLot2Search(search));
  };

export const setUserObject2Usage =
  (useObect: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setObject2(useObect));
  };

export const setUserLot2Usage =
  (useLOt: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setLot2(useLOt));
  };

export const setUserExpDateUsage =
  (useExpDate: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setExpDate(useExpDate));
  };

export const setUserExpDate2Usage =
  (useExpDate2: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(userSlice.actions.setExpDate2(useExpDate2));
  };

  export const setUserName = 
  (name: string): AppThunk => 
    async (dispatch: AppDispatch) => { 
      dispatch(userSlice.actions.setUserName(name));
  };

export default userSlice.reducer;

